import { reactive } from '@legendapp/state/react'
import { ScrollShadow } from '@nextui-org/scroll-shadow'
import { useNavigate } from '@tanstack/react-router'
import { AnimatePresence, motion, type Transition, useAnimationControls, type Variants } from 'framer-motion'
import { isEmpty } from 'radash'
import { FC, Fragment, useEffect } from 'react'
import { useMediaQuery } from 'usehooks-ts'

import sideMiniImage from '~in-app/assets/side-mini.png'
import { ConsoleLayout, IconButton, Menubar } from '~in-app/components'
import { ConsoleSidebar } from '~in-app/components/layout/console/sidebar/Context'
import { auth$ } from '~in-app/legend-state'
import { cn, formatUrlPathname } from '~in-app/lib/utils'
import { routerList } from '~in-app/routerList'

import { MenuRecursiveRender } from './MenuRecursiveRender'
import { MenuSection } from './MenuSection'

const transition: Transition = {
  type: 'spring',
  damping: 15,
}
const animationVariants: Variants = {
  init: ({ isSidebarOpen }) => ({
    width: isSidebarOpen ? '288px' : '100px',
    transition,
  }),
  maximize: {
    width: '288px',
    transition,
  },
  minimize: {
    width: '100px',
    transition,
  },
  exit: {
    width: '0px',
    paddingLeft: '0px',
    paddingRight: '0px',
    transition,
  },
}
const toggleButtonAnimationVairiants: Variants = {
  init: {
    display: 'unset',
    opacity: 1,
  },
  maximize: {
    display: 'unset',
    opacity: 1,
  },
  minimize: {
    display: 'unset',
    opacity: 1,
  },
  exit: {
    display: 'none',
    opacity: 0,
  },
}
const toggleAnimationVariants: Variants = {
  init: ({ isSidebarOpen }) => ({
    opacity: 1,
    rotate: isSidebarOpen ? 360 : 180,
    transition,
  }),
  maximize: {
    opacity: 1,
    rotate: 360,
    transition,
  },
  minimize: {
    opacity: 1,
    rotate: 180,
    transition,
  },
  exit: {
    opacity: 0,
    transition,
  },
}
const titleExpandAnimationVariants: Variants = {
  init: ({ isSidebarOpen }) => ({
    display: isSidebarOpen ? 'unset' : 'none',
    scale: isSidebarOpen ? 1 : 0,
    opacity: isSidebarOpen ? 1 : 0,
    x: isSidebarOpen ? 0 : -50,
  }),
  maximize: {
    display: 'unset',
    opacity: 1,
    scale: 1,
    x: 0,
    transition: {
      delay: 0.3,
      ease: 'easeInOut',
    },
  },
  minimize: {
    display: 'none',
    opacity: 0,
    scale: 0,
    x: -50,
    transition: {
      ease: 'easeInOut',
    },
  },
  exit: {
    opacity: 0,
  },
}
const titleCollapseAnimationVariants: Variants = {
  init: ({ isSidebarOpen }) => ({
    display: isSidebarOpen ? 'none' : 'unset',
    opacity: isSidebarOpen ? 0 : 1,
    scale: isSidebarOpen ? 0 : 1,
    x: isSidebarOpen ? -50 : 0,
    transition: {
      ease: 'easeInOut',
    },
  }),
  maximize: {
    display: 'none',
    opacity: 0,
    scale: 0,
    x: -50,
    transition: {
      ease: 'easeInOut',
    },
  },
  minimize: {
    display: 'unset',
    opacity: 1,
    scale: 1,
    x: 0,
    transition: {
      delay: 0.3,
      ease: 'easeInOut',
    },
  },
  exit: {
    opacity: 0,
  },
}

const ReactiveMenubar = reactive(Menubar)

const Sidebar: FC = () => {
  const navigate = useNavigate()
  const controls = useAnimationControls()
  const layoutContext = ConsoleLayout.useLayoutContext()
  const sidebarState$ = ConsoleSidebar.useSidebarState()
  const show = useMediaQuery('(min-width:640px)')

  useEffect(() => {
    const isSidebarOpenDispose = layoutContext.state$.isSidebarOpen.onChange((params) => {
      controls.start(params.value ? 'maximize' : 'minimize')
    })

    return () => {
      isSidebarOpenDispose()
    }
  }, [])

  return (
    <AnimatePresence>
      {
        !show
          ? (
              <motion.span
                key="ConsoleSidebarEmpty"
                className="hidden"
              />
            )
          : (
              <motion.div
                key="ConsoleSidebarMain"
                animate={controls}
                custom={{ isSidebarOpen: layoutContext.state$.isSidebarOpen.get() }}
                exit="exit"
                initial="init"
                variants={animationVariants}
                className={
                  cn('relative z-[11] flex h-screen flex-col border-r-2 bg-background/95 px-3 py-4 backdrop-blur supports-[backdrop-filter]:bg-background/70', {
                    'hidden': !show,
                  })
                }
              >
                <motion.span variants={toggleButtonAnimationVairiants}>
                  <IconButton
                    className="absolute -right-5 top-2.5"
                    color="muted"
                    onClick={() => layoutContext.state$.toggleSidebar()}
                  >
                    <svg
                      height="24"
                      viewBox="0 0 24 24"
                      width="24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <motion.path
                        custom={{ isSidebarOpen: layoutContext.state$.isSidebarOpen.get() }}
                        d="m10 17l5-5m0 0l-5-5"
                        fill="none"
                        stroke="currentColor"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2.5"
                        variants={toggleAnimationVariants}
                        transition={
                          {
                            duration: 0.5,
                            ease: 'easeInOut',
                          }
                        }
                      />
                    </svg>
                  </IconButton>
                </motion.span>

                <div
                  className="cursor-pointer select-none text-center"
                  onClick={
                    () => {
                      navigate({ to: '/console' })
                    }
                  }
                >
                  <motion.div
                    className="text-center text-3xl"
                    custom={{ isSidebarOpen: layoutContext.state$.isSidebarOpen.get() }}
                    variants={titleExpandAnimationVariants}
                  >
                    <div className="align-center flex items-center justify-center gap-2 self-center">
                      <img
                        alt="Sidebar Mini"
                        className="mb-2"
                        height="50px"
                        src={sideMiniImage}
                        width="50px"
                      />

                      <span>PLM</span>
                    </div>
                  </motion.div>

                  <motion.img
                    alt="Sidebar Mini"
                    className="text-center text-3xl"
                    custom={{ isSidebarOpen: layoutContext.state$.isSidebarOpen.get() }}
                    height="50px"
                    src={sideMiniImage}
                    variants={titleCollapseAnimationVariants}
                    width="50px"
                  />
                </div>

                <ScrollShadow
                  hideScrollBar
                  className="h-full"
                >
                  <ReactiveMenubar
                    ignoreDefaultStyle
                    $value={sidebarState$.thisMenuOpened}
                    onValueChange={sidebarState$.thisMenuOpened.set}
                  >
                    <ConsoleSidebar.Provider state$={sidebarState$}>
                      <nav className="mt-8">
                        <ul className="flex flex-col gap-2 px-2">
                          {
                            (routerList['console']?.items || []).filter((rl) => {
                              const hasValidPrivileges = (item: any) => {
                                if (item.children && item.children.length > 0) {
                                  const validChildren = item.children.filter((child: any) => hasValidPrivileges(child))

                                  if (validChildren.length > 0) {
                                    item.children = validChildren
                                    return true
                                  }
                                }
                                const hasItemPrivileges = (item?.privileges ?? []).some((privilege: string) =>
                                  auth$.privileges.get()?.has(privilege),
                                )
                                return hasItemPrivileges
                              }

                              if (hasValidPrivileges(rl)) {
                                return true
                              }
                              return null
                            })
                              .filter(Boolean)
                              .map(({ sectionLabel, children, ...props }) => {
                                const hasSectionLabel = !isEmpty(sectionLabel)
                                const rootPath = routerList['console'].rootPath
                                const path = formatUrlPathname(rootPath + (props.path || ''))

                                return (
                                  <Fragment key={`SidebarMenus_${props.label || sectionLabel}_${props.path}`}>
                                    {
                                      hasSectionLabel && (
                                        <MenuSection
                                          key={`MenuSection_${sectionLabel}_${path}`}
                                          sectionLabel={sectionLabel}
                                        />
                                      )
                                    }

                                    <MenuRecursiveRender
                                      key={`MenuRender_${props.label}_${path}`}
                                      icon={props.icon}
                                      isSection={hasSectionLabel}
                                      items={children}
                                      label={props.label}
                                      path={path}
                                    />
                                  </Fragment>
                                )
                              })
                          }

                        </ul>
                      </nav>
                    </ConsoleSidebar.Provider>
                  </ReactiveMenubar>

                </ScrollShadow>
              </motion.div>
            )
      }
    </AnimatePresence>
  )
}
Sidebar.displayName = 'ConsoleSidebar'

export { Sidebar }
